/**
 * Task Management Component
 * Simplified, focused task management for GMB profiles
 * Shows critical tasks first, then default engagement tasks
 * 
 * @author Visibeen Team - 2024
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import StarIcon from '@mui/icons-material/Star';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import CriticalTasksService from '../../services/CriticalTasksService';
import GMBService from '../../services/GMBService';
import PhotoUploadPopup from './popups/PhotoUploadPopup';
import CategoryManagementPopup from './popups/CategoryManagementPopup';
import BusinessHoursPopup from './popups/BusinessHoursPopup';
import MapLocationPopup from './popups/MapLocationPopup';
import VerificationPopup from './popups/VerificationPopup';
import logger from '../../utils/logger';

// ============================================
// Styled Components - Modern Premium Design
// ============================================

const TasksContainer = styled(Box)(({ theme }) => ({
    padding: '16px',
    maxWidth: '900px',
    margin: '0 auto',
    [theme.breakpoints.up('sm')]: {
        padding: '20px'
    },
    [theme.breakpoints.up('md')]: {
        padding: '24px'
    }
}));

const HeaderSection = styled(Box)(({ theme }) => ({
    marginBottom: '20px',
    textAlign: 'center',
    [theme.breakpoints.up('sm')]: {
        marginBottom: '28px'
    },
    [theme.breakpoints.up('md')]: {
        marginBottom: '32px'
    }
}));

const TaskCard = styled(Card)(({ theme, priority }) => ({
    marginBottom: '12px',
    borderRadius: '12px',
    border: priority === 'Critical'
        ? '2px solid #ef4444'
        : '1px solid #e2e8f0',
    boxShadow: priority === 'Critical'
        ? '0 4px 16px rgba(239, 68, 68, 0.12)'
        : '0 2px 6px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.3s ease',
    overflow: 'hidden',
    WebkitTapHighlightColor: 'transparent',
    '&:active': {
        transform: 'scale(0.995)'
    },
    [theme.breakpoints.up('sm')]: {
        marginBottom: '16px',
        borderRadius: '16px'
    },
    [theme.breakpoints.up('md')]: {
        boxShadow: priority === 'Critical'
            ? '0 4px 20px rgba(239, 68, 68, 0.15)'
            : '0 2px 8px rgba(0, 0, 0, 0.05)',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: priority === 'Critical'
                ? '0 8px 30px rgba(239, 68, 68, 0.2)'
                : '0 8px 24px rgba(0, 0, 0, 0.1)'
        },
        '&:active': {
            transform: 'translateY(-1px)'
        }
    }
}));

const TaskHeader = styled(Box)(({ theme, priority }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '16px',
    background: priority === 'Critical'
        ? 'linear-gradient(135deg, #fef2f2 0%, #fff 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #fff 100%)',
    borderBottom: '1px solid #f1f5f9',
    [theme.breakpoints.up('sm')]: {
        alignItems: 'center',
        gap: '16px',
        padding: '20px 24px'
    }
}));

const TaskIcon = styled(Box)(({ theme, priority }) => ({
    width: '40px',
    height: '40px',
    minWidth: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    background: priority === 'Critical'
        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
        : 'linear-gradient(135deg, #0b91d6 0%, #0284c7 100%)',
    color: 'white',
    boxShadow: priority === 'Critical'
        ? '0 4px 10px rgba(239, 68, 68, 0.25)'
        : '0 4px 10px rgba(11, 145, 214, 0.25)',
    [theme.breakpoints.up('sm')]: {
        width: '48px',
        height: '48px',
        minWidth: '48px',
        borderRadius: '12px',
        fontSize: '24px',
        boxShadow: priority === 'Critical'
            ? '0 4px 12px rgba(239, 68, 68, 0.3)'
            : '0 4px 12px rgba(11, 145, 214, 0.3)'
    }
}));

const TaskContent = styled(CardContent)(({ theme }) => ({
    padding: '16px !important',
    [theme.breakpoints.up('sm')]: {
        padding: '20px 24px !important'
    }
}));

const PriorityChip = styled(Chip)(({ theme, priority }) => {
    const colors = {
        Critical: { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
        High: { bg: '#fff7ed', text: '#ea580c', border: '#fed7aa' },
        Medium: { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
        Low: { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' }
    };
    const c = colors[priority] || colors.Medium;
    return {
        backgroundColor: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
        fontWeight: 600,
        fontSize: '10px',
        height: '22px',
        [theme.breakpoints.up('sm')]: {
            fontSize: '11px',
            height: '24px'
        }
    };
});

const ActionButton = styled(Button)(({ theme, variant }) => ({
    borderRadius: '8px',
    padding: '10px 16px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '13px',
    minHeight: '44px',
    flex: 1,
    boxShadow: variant === 'contained' ? '0 4px 10px rgba(11, 145, 214, 0.2)' : 'none',
    WebkitTapHighlightColor: 'transparent',
    '&:active': {
        transform: 'scale(0.98)'
    },
    [theme.breakpoints.up('sm')]: {
        borderRadius: '10px',
        padding: '10px 24px',
        fontSize: '14px',
        minHeight: '42px',
        flex: 'none',
        boxShadow: variant === 'contained' ? '0 4px 12px rgba(11, 145, 214, 0.25)' : 'none'
    },
    [theme.breakpoints.up('md')]: {
        '&:hover': {
            boxShadow: variant === 'contained' ? '0 6px 16px rgba(11, 145, 214, 0.35)' : 'none'
        },
        '&:active': {
            transform: 'none'
        }
    }
}));

const ExpertButton = styled(Button)(({ theme }) => ({
    borderRadius: '8px',
    padding: '10px 16px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '12px',
    minHeight: '44px',
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: 'white',
    marginLeft: 0,
    marginTop: '8px',
    width: '100%',
    WebkitTapHighlightColor: 'transparent',
    '&:active': {
        transform: 'scale(0.98)'
    },
    [theme.breakpoints.up('sm')]: {
        borderRadius: '10px',
        padding: '10px 20px',
        fontSize: '13px',
        minHeight: '42px',
        marginLeft: '12px',
        marginTop: 0,
        width: 'auto'
    },
    [theme.breakpoints.up('md')]: {
        '&:hover': {
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
        },
        '&:active': {
            transform: 'none'
        }
    }
}));

const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: '40px 20px',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
    borderRadius: '16px',
    border: '1px solid #bbf7d0',
    [theme.breakpoints.up('sm')]: {
        padding: '60px 24px',
        borderRadius: '20px'
    }
}));

// ============================================
// Main Component
// ============================================

function TaskManagement({
    gmbData,
    selectedProfile,
    onRefresh,
    onPhotosUploaded, // ✅ NEW: Callback to update photos locally without API refresh
    isLoading
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [completedTasks, setCompletedTasks] = useState(new Set());
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [reviewLinkDialog, setReviewLinkDialog] = useState(false);
    const [expertDialog, setExpertDialog] = useState(false);
    const [photoUploadDialog, setPhotoUploadDialog] = useState(false);
    const [currentPhotoTask, setCurrentPhotoTask] = useState(null);
    const [categoryDialog, setCategoryDialog] = useState(false);
    const [currentCategoryTask, setCurrentCategoryTask] = useState(null);
    const [hoursDialog, setHoursDialog] = useState(false);
    const [currentHoursTask, setCurrentHoursTask] = useState(null);
    const [mapLocationDialog, setMapLocationDialog] = useState(false);
    const [currentMapTask, setCurrentMapTask] = useState(null);
    const [verificationDialog, setVerificationDialog] = useState(false);
    const [currentVerificationTask, setCurrentVerificationTask] = useState(null);

    // State for fresh newReviewUri fetched from Google API
    const [freshNewReviewUri, setFreshNewReviewUri] = useState(null);

    // Fetch fresh newReviewUri from Google API if not available in gmbData
    useEffect(() => {
        const fetchNewReviewUri = async () => {
            // Check if we already have newReviewUri in gmbData
            const existingUri = gmbData?.metadata?.newReviewUri || gmbData?.newReviewUri;
            if (existingUri) {
                logger.log('[TaskManagement] Using existing newReviewUri:', existingUri);
                setFreshNewReviewUri(existingUri);
                return;
            }

            // Get locationId from selectedProfile or gmbData
            const locationId = selectedProfile?.name?.split('/').pop() ||
                selectedProfile?.profileKey ||
                gmbData?.name?.split('/').pop();

            if (!locationId) return;

            try {
                logger.log('[TaskManagement] Fetching fresh location data for newReviewUri...');
                const freshLocationData = await GMBService.getLocation(locationId);

                const newUri = freshLocationData?.metadata?.newReviewUri;
                if (newUri) {
                    logger.log('[TaskManagement] ✅ Got fresh newReviewUri:', newUri);
                    setFreshNewReviewUri(newUri);
                } else {
                    logger.log('[TaskManagement] newReviewUri not available from Google API');
                }
            } catch (err) {
                logger.warn('[TaskManagement] Failed to fetch fresh location data:', err?.message);
            }
        };

        if (gmbData && !isLoading) {
            fetchNewReviewUri();
        }
    }, [gmbData, selectedProfile, isLoading]);

    // Get tasks from service (with null safety)
    const taskResult = gmbData ? CriticalTasksService.getProfileTasks(gmbData) : { criticalTasks: [], defaultTasks: [], allTasks: [], hasCriticalTasks: false };
    const { criticalTasks, defaultTasks, allTasks, hasCriticalTasks } = taskResult;

    logger.log('[TaskManagement] Tasks loaded:', {
        critical: criticalTasks?.length || 0,
        default: defaultTasks?.length || 0,
        profile: selectedProfile?.title
    });

    // ============================================
    // Handlers
    // ============================================

    const handleTaskAction = useCallback((task) => {
        logger.log('[TaskManagement] Task action clicked:', task.id);

        switch (task.actionType) {
            case 'verification':
                // Open verification popup modal for in-app verification
                setCurrentVerificationTask(task);
                setVerificationDialog(true);
                break;

            case 'external':
                window.open(task.actionUrl || 'https://business.google.com', '_blank');
                break;

            case 'map':
                // Open map location picker popup modal
                setCurrentMapTask(task);
                setMapLocationDialog(true);
                break;

            case 'address':
                window.open('https://business.google.com', '_blank');
                setSnackbar({
                    open: true,
                    message: 'Opening Google Business Profile to edit address...',
                    severity: 'info'
                });
                break;

            case 'phone':
                window.open('https://business.google.com', '_blank');
                break;

            case 'category':
                // Open category management popup modal
                setCurrentCategoryTask(task);
                setCategoryDialog(true);
                break;

            case 'hours':
                // Open business hours popup modal
                setCurrentHoursTask(task);
                setHoursDialog(true);
                break;

            case 'photos':
                // Open photo upload popup modal
                setCurrentPhotoTask(task);
                setPhotoUploadDialog(true);
                break;

            case 'reviews':
                setReviewLinkDialog(true);
                break;

            default:
                window.open('https://business.google.com', '_blank');
        }
    }, [gmbData]);

    const handleExpertHelp = useCallback(() => {
        setExpertDialog(true);
    }, []);

    // Helper to get the best review link (prefer freshNewReviewUri)
    const getReviewLink = useCallback(() => {
        // Priority 1: Fresh newReviewUri from Google API
        if (freshNewReviewUri) {
            return freshNewReviewUri;
        }
        // Priority 2: Use CriticalTasksService fallback
        return CriticalTasksService.generateReviewLink(gmbData);
    }, [freshNewReviewUri, gmbData]);

    const handleCopyReviewLink = useCallback(() => {
        const reviewLink = getReviewLink();
        navigator.clipboard.writeText(reviewLink);
        setSnackbar({ open: true, message: 'Review link copied to clipboard!', severity: 'success' });
    }, [getReviewLink]);

    const handleShareWhatsApp = useCallback(() => {
        const reviewLink = getReviewLink();
        const message = `Hi! Please leave us a review on Google: ${reviewLink}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
        setReviewLinkDialog(false);
    }, [getReviewLink]);

    const markTaskComplete = useCallback((taskId) => {
        setCompletedTasks(prev => new Set([...prev, taskId]));
        setSnackbar({ open: true, message: 'Task marked as complete!', severity: 'success' });
    }, []);

    const handlePhotoUploadComplete = useCallback((completionData) => {
        logger.log('[TaskManagement] Photo upload completed:', completionData);
        setPhotoUploadDialog(false);
        setCurrentPhotoTask(null);
        if (completionData?.completed) {
            const photosCount = completionData.photosUploaded || completionData.totalPhotos || 0;
            setSnackbar({
                open: true,
                message: `Successfully uploaded ${photosCount} photo${photosCount !== 1 ? 's' : ''} to your GMB profile!`,
                severity: 'success'
            });

            // ✅ FIX: Update gmbData locally instead of full page refresh
            // Extract uploaded photo info from completion data
            if (onPhotosUploaded && completionData.photoUrls) {
                logger.log('[TaskManagement] 📸 Updating local state with uploaded photos');
                onPhotosUploaded(completionData.photoUrls);
            }

            // Mark task as complete after short delay to allow state update
            setTimeout(() => {
                if (currentPhotoTask?.id) {
                    markTaskComplete(currentPhotoTask.id);
                }
            }, 500);
        }
    }, [currentPhotoTask, markTaskComplete, onPhotosUploaded]);

    const handleCategoryComplete = useCallback((completionData) => {
        logger.log('[TaskManagement] Category update completed:', completionData);
        setCategoryDialog(false);
        setCurrentCategoryTask(null);
        if (completionData?.completed) {
            setSnackbar({
                open: true,
                message: 'Business category updated successfully!',
                severity: 'success'
            });
            // Mark the task as complete
            if (currentCategoryTask?.id) {
                markTaskComplete(currentCategoryTask.id);
            }
            // Refresh if callback available
            if (onRefresh) {
                onRefresh();
            }
        }
    }, [currentCategoryTask, markTaskComplete, onRefresh]);

    const handleHoursComplete = useCallback((completionData) => {
        logger.log('[TaskManagement] Hours update completed:', completionData);
        setHoursDialog(false);
        setCurrentHoursTask(null);
        if (completionData?.completed) {
            setSnackbar({
                open: true,
                message: 'Business hours updated successfully! Your business will be marked as open.',
                severity: 'success'
            });
            // Mark the task as complete
            if (currentHoursTask?.id) {
                markTaskComplete(currentHoursTask.id);
            }
            // Refresh if callback available
            if (onRefresh) {
                onRefresh();
            }
        }
    }, [currentHoursTask, markTaskComplete, onRefresh]);

    const handleMapLocationComplete = useCallback((completionData) => {
        logger.log('[TaskManagement] Map location update completed:', completionData);
        setMapLocationDialog(false);
        setCurrentMapTask(null);
        if (completionData?.completed) {
            setSnackbar({
                open: true,
                message: 'Location updated successfully! Changes may take a few minutes to appear on Google Maps.',
                severity: 'success'
            });
            // Mark the task as complete
            if (currentMapTask?.id) {
                markTaskComplete(currentMapTask.id);
            }
            // Refresh if callback available
            if (onRefresh) {
                onRefresh();
            }
        }
    }, [currentMapTask, markTaskComplete, onRefresh]);

    const handleVerificationComplete = useCallback((completionData) => {
        logger.log('[TaskManagement] Verification completed:', completionData);
        setVerificationDialog(false);
        setCurrentVerificationTask(null);
        if (completionData?.completed) {
            setSnackbar({
                open: true,
                message: 'Congratulations! Your business is now verified on Google!',
                severity: 'success'
            });
            // Mark the task as complete
            if (currentVerificationTask?.id) {
                markTaskComplete(currentVerificationTask.id);
            }
            // Refresh if callback available
            if (onRefresh) {
                onRefresh();
            }
        }
    }, [currentVerificationTask, markTaskComplete, onRefresh]);

    // ============================================
    // Render Functions
    // ============================================

    const renderTaskIcon = (task) => {
        if (task.priority === 'Critical') {
            return <ErrorIcon sx={{ fontSize: 24 }} />;
        }
        if (task.actionType === 'photos') {
            return <PhotoCameraIcon sx={{ fontSize: 24 }} />;
        }
        if (task.actionType === 'reviews') {
            return <StarIcon sx={{ fontSize: 24 }} />;
        }
        return <span>{task.icon}</span>;
    };

    const renderTask = (task, index) => {
        const isCompleted = completedTasks.has(task.id);

        if (isCompleted) return null;

        return (
            <TaskCard key={task.id} priority={task.priority}>
                <TaskHeader priority={task.priority}>
                    <TaskIcon priority={task.priority}>
                        {renderTaskIcon(task)}
                    </TaskIcon>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 700,
                            color: '#1e293b',
                            fontSize: { xs: '15px', sm: '17px' },
                            mb: 0.5,
                            lineHeight: 1.3
                        }}>
                            {task.icon} {task.title}
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap'
                        }}>
                            <PriorityChip
                                label={task.priority}
                                priority={task.priority}
                                size="small"
                            />
                            <Typography variant="caption" sx={{
                                color: '#64748b',
                                fontSize: { xs: '11px', sm: '12px' }
                            }}>
                                {task.category}
                            </Typography>
                        </Box>
                    </Box>
                </TaskHeader>

                <TaskContent>
                    <Typography sx={{
                        color: '#475569',
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.6,
                        mb: task.progress !== undefined ? 2 : 3
                    }}>
                        {task.description}
                    </Typography>

                    {/* Progress Bar for tasks with progress tracking */}
                    {task.progress !== undefined && (
                        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{
                                    fontSize: { xs: '12px', sm: '13px' },
                                    color: '#64748b',
                                    fontWeight: 500
                                }}>
                                    Progress: {task.currentCount}/{task.targetCount}
                                </Typography>
                                <Typography sx={{
                                    fontSize: { xs: '12px', sm: '13px' },
                                    color: '#0b91d6',
                                    fontWeight: 600
                                }}>
                                    {task.progress}%
                                </Typography>
                            </Box>
                            <Box sx={{
                                width: '100%',
                                height: { xs: '6px', sm: '8px' },
                                backgroundColor: '#e2e8f0',
                                borderRadius: '4px',
                                overflow: 'hidden'
                            }}>
                                <Box sx={{
                                    width: `${task.progress}%`,
                                    height: '100%',
                                    background: task.progress >= 80
                                        ? 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)'
                                        : task.progress >= 40
                                            ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
                                            : 'linear-gradient(90deg, #0b91d6 0%, #0284c7 100%)',
                                    borderRadius: '4px',
                                    transition: 'width 0.5s ease'
                                }} />
                            </Box>
                            <Typography sx={{
                                fontSize: { xs: '11px', sm: '12px' },
                                color: '#94a3b8',
                                mt: 0.5
                            }}>
                                {task.remaining} more to complete goal
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 1.5 }
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            width: { xs: '100%', sm: 'auto' }
                        }}>
                            <ActionButton
                                variant="contained"
                                onClick={() => handleTaskAction(task)}
                                endIcon={!['photos', 'category', 'hours', 'map', 'reviews', 'verification'].includes(task.actionType) ? <OpenInNewIcon sx={{ fontSize: { xs: 14, sm: 16 } }} /> : null}
                                sx={{
                                    background: task.priority === 'Critical'
                                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                        : 'linear-gradient(135deg, #0b91d6 0%, #0284c7 100%)'
                                }}
                            >
                                {task.actionLabel}
                            </ActionButton>
                        </Box>

                        {task.expertOption && (
                            <ExpertButton
                                onClick={handleExpertHelp}
                                startIcon={<WhatsAppIcon />}
                            >
                                Ask Our Expert
                            </ExpertButton>
                        )}

                        <Tooltip title="Mark as complete">
                            <IconButton
                                onClick={() => markTaskComplete(task.id)}
                                sx={{
                                    ml: { xs: 0, sm: 'auto' },
                                    color: '#94a3b8',
                                    minHeight: '44px',
                                    minWidth: '44px',
                                    '&:hover': { color: '#22c55e' }
                                }}
                            >
                                <CheckCircleIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </TaskContent>
            </TaskCard>
        );
    };

    // ============================================
    // Main Render
    // ============================================

    if (isLoading) {
        return (
            <TasksContainer>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <CircularProgress size={48} sx={{ color: '#0b91d6' }} />
                    <Typography sx={{ mt: 2, color: '#64748b' }}>
                        Analyzing your profile...
                    </Typography>
                </Box>
            </TasksContainer>
        );
    }

    const visibleTasks = allTasks.filter(t => !completedTasks.has(t.id));

    return (
        <TasksContainer>
            {/* Header */}
            <HeaderSection>
                <Typography variant="h4" sx={{
                    fontWeight: 800,
                    color: '#0f172a',
                    mb: 1,
                    fontSize: { xs: '22px', sm: '26px', md: '28px' }
                }}>
                    {hasCriticalTasks ? '⚡ Action Required' : '📋 Your Tasks'}
                </Typography>
                <Typography sx={{
                    color: '#64748b',
                    fontSize: { xs: '13px', sm: '14px', md: '15px' },
                    px: { xs: 1, sm: 0 }
                }}>
                    {hasCriticalTasks
                        ? 'Complete these critical tasks to improve your profile visibility'
                        : 'Keep your profile active with these engagement tasks'}
                </Typography>
                {selectedProfile?.title && (
                    <Chip
                        label={selectedProfile.title}
                        sx={{
                            mt: 2,
                            background: '#f1f5f9',
                            fontWeight: 500,
                            fontSize: { xs: '12px', sm: '13px' },
                            maxWidth: '90%',
                            '& .MuiChip-label': {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }
                        }}
                    />
                )}
            </HeaderSection>

            {/* Critical Alert */}
            {hasCriticalTasks && (
                <Alert
                    severity="warning"
                    icon={<WarningIcon />}
                    sx={{
                        mb: { xs: 2, sm: 3 },
                        borderRadius: { xs: '10px', sm: '12px' },
                        fontSize: { xs: '13px', sm: '14px' },
                        '& .MuiAlert-icon': { color: '#ea580c' }
                    }}
                >
                    <strong>{criticalTasks.length} critical issue{criticalTasks.length > 1 ? 's' : ''}</strong>
                    {' '}found that may be affecting your visibility on Google.
                </Alert>
            )}

            {/* Tasks List */}
            {visibleTasks.length > 0 ? (
                <Box>
                    {visibleTasks.map((task, index) => renderTask(task, index))}
                </Box>
            ) : (
                <EmptyState>
                    <CheckCircleIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: '#22c55e', mb: 2 }} />
                    <Typography variant="h5" sx={{
                        fontWeight: 700,
                        color: '#166534',
                        mb: 1,
                        fontSize: { xs: '18px', sm: '22px' }
                    }}>
                        All Tasks Complete! 🎉
                    </Typography>
                    <Typography sx={{
                        color: '#15803d',
                        fontSize: { xs: '13px', sm: '14px' }
                    }}>
                        Your profile is in great shape. Check back later for new tasks.
                    </Typography>
                </EmptyState>
            )}

            {/* Review Link Dialog */}
            <Dialog
                open={reviewLinkDialog}
                onClose={() => setReviewLinkDialog(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : '16px',
                        m: isMobile ? 0 : 2
                    }
                }}
            >
                <DialogTitle sx={{
                    fontWeight: 700,
                    fontSize: { xs: '18px', sm: '20px' },
                    pt: { xs: 2, sm: 2 }
                }}>
                    📝 Get Customer Reviews
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
                    <Typography sx={{
                        mb: 2,
                        color: '#475569',
                        fontSize: { xs: '13px', sm: '14px' }
                    }}>
                        Share this link or QR code with your happy customers to collect reviews:
                    </Typography>

                    {/* QR Code Section */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: { xs: 2, sm: 3 },
                        mb: { xs: 2, sm: 3 },
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0'
                    }}>
                        <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(getReviewLink())}`}
                            alt="Review QR Code"
                            style={{
                                width: isMobile ? '160px' : '200px',
                                height: isMobile ? '160px' : '200px',
                                borderRadius: '8px'
                            }}
                        />
                        <Typography sx={{
                            mt: 2,
                            color: '#64748b',
                            fontSize: { xs: '12px', sm: '13px' },
                            textAlign: 'center'
                        }}>
                            Scan this QR code to leave a review
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            sx={{
                                mt: 1,
                                textTransform: 'none',
                                borderRadius: '8px',
                                minHeight: '40px'
                            }}
                            onClick={async () => {
                                try {
                                    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(getReviewLink())}`;
                                    const response = await fetch(qrUrl);
                                    const blob = await response.blob();
                                    const blobUrl = URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.download = 'review-qr-code.png';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    URL.revokeObjectURL(blobUrl);
                                } catch (err) {
                                    console.error('Failed to download QR code:', err);
                                    // Fallback: open in new tab
                                    window.open(`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(getReviewLink())}`, '_blank');
                                }
                            }}
                        >
                            📥 Download QR Code
                        </Button>
                    </Box>

                    <TextField
                        fullWidth
                        value={getReviewLink()}
                        InputProps={{
                            readOnly: true,
                            sx: { fontSize: { xs: '13px', sm: '14px' } },
                            endAdornment: (
                                <IconButton
                                    onClick={handleCopyReviewLink}
                                    sx={{ minWidth: '44px', minHeight: '44px' }}
                                >
                                    <ContentCopyIcon />
                                </IconButton>
                            )
                        }}
                        sx={{ mb: 2 }}
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<WhatsAppIcon />}
                        onClick={handleShareWhatsApp}
                        sx={{
                            background: '#25d366',
                            '&:hover': { background: '#128c7e' },
                            borderRadius: '10px',
                            py: 1.5,
                            fontWeight: 600,
                            minHeight: '48px'
                        }}
                    >
                        Share via WhatsApp
                    </Button>
                </DialogContent>
                <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
                    <Button
                        onClick={() => setReviewLinkDialog(false)}
                        sx={{ minHeight: '44px' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Expert Help Dialog */}
            <Dialog
                open={expertDialog}
                onClose={() => setExpertDialog(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : '16px',
                        m: isMobile ? 0 : 2
                    }
                }}
            >
                <DialogTitle sx={{
                    fontWeight: 700,
                    fontSize: { xs: '18px', sm: '20px' }
                }}>
                    🎯 Get Expert Assistance
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
                    <Typography sx={{
                        mb: 2,
                        color: '#475569',
                        fontSize: { xs: '13px', sm: '14px' }
                    }}>
                        Our experts can help you with profile verification and setup. This service may require:
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                            fontSize: { xs: '13px', sm: '14px' }
                        }}>
                            • Business identity documents
                        </Typography>
                        <Typography sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                            fontSize: { xs: '13px', sm: '14px' }
                        }}>
                            • Profile access permissions
                        </Typography>
                        <Typography sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: { xs: '13px', sm: '14px' }
                        }}>
                            • Service charges may apply
                        </Typography>
                    </Box>
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<WhatsAppIcon />}
                        onClick={() => {
                            window.open('https://wa.me/919876543210?text=Hi! I need expert help with my Google Business Profile.', '_blank');
                            setExpertDialog(false);
                        }}
                        sx={{
                            background: '#25d366',
                            '&:hover': { background: '#128c7e' },
                            borderRadius: '10px',
                            py: 1.5,
                            fontWeight: 600,
                            minHeight: '48px'
                        }}
                    >
                        Contact Expert via WhatsApp
                    </Button>
                </DialogContent>
                <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
                    <Button
                        onClick={() => setExpertDialog(false)}
                        sx={{ minHeight: '44px' }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Photo Upload Dialog */}
            <Dialog
                open={photoUploadDialog}
                onClose={() => {
                    setPhotoUploadDialog(false);
                    setCurrentPhotoTask(null);
                }}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : '16px',
                        overflow: 'hidden',
                        m: isMobile ? 0 : 2
                    }
                }}
            >
                <PhotoUploadPopup
                    task={currentPhotoTask}
                    onComplete={handlePhotoUploadComplete}
                    onBack={() => {
                        setPhotoUploadDialog(false);
                        setCurrentPhotoTask(null);
                    }}
                    businessProfileData={gmbData || {}}
                    profileId={selectedProfile?.name?.split('/').pop() || selectedProfile?.profileKey}
                />
            </Dialog>

            {/* Category Management Dialog */}
            <Dialog
                open={categoryDialog}
                onClose={() => {
                    setCategoryDialog(false);
                    setCurrentCategoryTask(null);
                }}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : '16px',
                        overflow: 'hidden',
                        m: isMobile ? 0 : 2
                    }
                }}
            >
                <CategoryManagementPopup
                    task={currentCategoryTask}
                    onComplete={handleCategoryComplete}
                    onBack={() => {
                        setCategoryDialog(false);
                        setCurrentCategoryTask(null);
                    }}
                    businessProfileData={gmbData || {}}
                    profileId={selectedProfile?.name?.split('/').pop() || selectedProfile?.profileKey}
                />
            </Dialog>

            {/* Business Hours Dialog */}
            <Dialog
                open={hoursDialog}
                onClose={() => {
                    setHoursDialog(false);
                    setCurrentHoursTask(null);
                }}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : '16px',
                        overflow: 'hidden',
                        m: isMobile ? 0 : 2
                    }
                }}
            >
                <BusinessHoursPopup
                    task={currentHoursTask}
                    onComplete={handleHoursComplete}
                    onBack={() => {
                        setHoursDialog(false);
                        setCurrentHoursTask(null);
                    }}
                    businessProfileData={gmbData || {}}
                    profileId={selectedProfile?.name?.split('/').pop() || selectedProfile?.profileKey}
                />
            </Dialog>

            {/* Map Location Dialog */}
            <Dialog
                open={mapLocationDialog}
                onClose={() => {
                    setMapLocationDialog(false);
                    setCurrentMapTask(null);
                }}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : '16px',
                        overflow: 'hidden',
                        m: isMobile ? 0 : 2
                    }
                }}
            >
                <MapLocationPopup
                    task={currentMapTask}
                    onComplete={handleMapLocationComplete}
                    onBack={() => {
                        setMapLocationDialog(false);
                        setCurrentMapTask(null);
                    }}
                    businessProfileData={gmbData || {}}
                    profileId={selectedProfile?.name?.split('/').pop() || selectedProfile?.profileKey}
                />
            </Dialog>

            {/* Verification Dialog */}
            <Dialog
                open={verificationDialog}
                onClose={() => {
                    setVerificationDialog(false);
                    setCurrentVerificationTask(null);
                }}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : '16px',
                        overflow: 'hidden',
                        m: isMobile ? 0 : 2
                    }
                }}
            >
                <VerificationPopup
                    task={currentVerificationTask}
                    onComplete={handleVerificationComplete}
                    onBack={() => {
                        setVerificationDialog(false);
                        setCurrentVerificationTask(null);
                    }}
                    businessProfileData={gmbData || {}}
                    profileId={selectedProfile?.name?.split('/').pop() || selectedProfile?.profileKey}
                />
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    sx={{ borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </TasksContainer>
    );
}

export default TaskManagement;
